import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, getObsScore, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import ObsNumericStepper from './obsNumericStepper';
import renderAcceptSection from './renderAcceptSection';
import {useTheme} from '@mui/material/styles';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import {obsStyles} from '../obsStyle';

function ObsBloodPressure(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;
    const lowerExtremitiesOptions = config?.observations?.lowerExtremitiesOptions;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [systolicPressure, setSystolicPressure] = React.useState(allowDefaultValue ? props.systolicPressure : null);
    const [diastolicPressure, setDiastolicPressure] = React.useState(allowDefaultValue ? props.diastolicPressure : null);
    const [arm, setArm] = React.useState(allowDefaultValue ? props.arm : null);
    const [optionalBP, setOptionalBP] = React.useState(props.optinalBP);
    const [optionalSystolicPressure, setOptionalSystolicPressure] = React.useState(allowDefaultValue ? props.optionalSystolicPressure : null);
    const [optionalDiastolicPressure, setOptionalDiastolicPressure] = React.useState(allowDefaultValue ? props.optionalDiastolicPressure : null);
    const [optionalArm, setOptionalArm] = React.useState(allowDefaultValue ? props.optionalArm : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    function setDefaults() {
        if (type === obsTypes.news) {
            setSystolicPressure(140);
            setDiastolicPressure(60);
            setOptionalSystolicPressure(0);
            setOptionalDiastolicPressure(0);
        } else if (type === obsTypes.pews) {
            if (ageRangeYears === obsAges._0_1) {
                setSystolicPressure(80);
                setDiastolicPressure(45);
                setOptionalSystolicPressure(0);
                setOptionalDiastolicPressure(0);
            } else if (ageRangeYears === obsAges._1_5) {
                setSystolicPressure(90);
                setDiastolicPressure(60);
                setOptionalSystolicPressure(0);
                setOptionalDiastolicPressure(0);
            } else if (ageRangeYears === obsAges._5_12) {
                setSystolicPressure(100);
                setDiastolicPressure(60);
                setOptionalSystolicPressure(0);
                setOptionalDiastolicPressure(0);
            } else if (ageRangeYears === obsAges._12_18) {
                setSystolicPressure(110);
                setDiastolicPressure(60);
                setOptionalSystolicPressure(0);
                setOptionalDiastolicPressure(0);
            }
        } else if (type === obsTypes.neuro) {
            if (ageRangeYears === obsAges._1_5) {
                setSystolicPressure(90);
                setDiastolicPressure(60);
                setOptionalSystolicPressure(0);
                setOptionalDiastolicPressure(0);
            } else if (ageRangeYears === obsAges._5_16) {
                setSystolicPressure(100);
                setDiastolicPressure(60);
                setOptionalSystolicPressure(0);
                setOptionalDiastolicPressure(0);
            } else {
                setSystolicPressure(140);
                setDiastolicPressure(60);
                setOptionalSystolicPressure(0);
                setOptionalDiastolicPressure(0);
            }
        }
    }

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setSystolicPressure(props.systolicPressure);
            setDiastolicPressure(props.diastolicPressure);
            setArm(props.arm);
            setOptionalBP(props.optionalBP);
            setOptionalSystolicPressure(props.optionalSystolicPressure);
            setOptionalDiastolicPressure(props.optionalDiastolicPressure);
            setOptionalArm(props.optionalArm);
        }
    }, []);

    useEffect(() => {
        if ((_.isUndefined(systolicPressure) || _.isNaN(systolicPressure)) && allowDefaultValue) {
            if (_.isUndefined(props.systolicPressure) || props.systolicPressure == null || _.isNaN(props.systolicPressure)) {
                setDefaults();
            } else {
                setSystolicPressure(props.systolicPressure);
                setDiastolicPressure(props.diastolicPressure);
                setOptionalSystolicPressure(props.optionalSystolicPressure);
                setOptionalDiastolicPressure(props.optionalDiastolicPressure);
            }
        }
    }, [
        type,
        ageRangeYears,
        props.systolicPressure,
        props.diastolicPressure,
        props.optionalSystolicPressure,
        props.optionalDiastolicPressure,
    ]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) {
            setArm(null);
            setOptionalArm(null);
            setSystolicPressure(null);
            setDiastolicPressure(null);
            setOptionalSystolicPressure(null);
            setOptionalDiastolicPressure(null);
        } else {
            setDefaults();
        }
        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) setStatus(obsStatuses.completed);
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValidPressure = (value) => {
            return !_.isNull(value) && value > 0;
        };

        const checkValue = (checkValidPressure(systolicPressure) && checkValidPressure(diastolicPressure) && !_.isNull(arm) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            if (optionalBP) {
                if (checkValidPressure(optionalDiastolicPressure) && checkValidPressure(optionalSystolicPressure) && !_.isNull(optionalArm)) {
                    acceptObs();
                }
            } else {
                acceptObs();
            }
        }
    }, [
        systolicPressure,
        diastolicPressure,
        arm,
        optionalBP,
        optionalDiastolicPressure,
        optionalSystolicPressure,
        optionalArm,
        status,
    ]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            if (type === obsTypes.pews) {
                lazyEmitOnChange(
                    {
                        status,
                        systolicPressure,
                        diastolicPressure,
                        arm,
                        // optionalBP,
                        // optionalSystolicPressure,
                        // optionalDiastolicPressure,
                        // optionalArm,
                        score,
                        accepted,
                        acceptDate,
                    },
                    props.onChange,
                );
            } else {
                lazyEmitOnChange(
                    {
                        status,
                        systolicPressure,
                        diastolicPressure,
                        arm,
                        optionalBP,
                        optionalSystolicPressure,
                        optionalDiastolicPressure,
                        optionalArm,
                        score,
                        accepted,
                        acceptDate,
                    },
                    props.onChange,
                );
            }
        }
    };
    useEffect(() => {
        emitChanged();
    }, [
        status,
        systolicPressure,
        diastolicPressure,
        arm,
        optionalBP,
        optionalSystolicPressure,
        optionalDiastolicPressure,
        optionalArm,
        score,
        accepted,
        acceptDate,
    ]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            newScore = getObsScore('bloodPresure', type, ageRangeYears, systolicPressure);

        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, systolicPressure, diastolicPressure, props.airType]);

    const onSystolicPressureChange = (value) => {
        setSystolicPressure(value);
        clearAccepted();
    };

    const onDiastolicPressureChange = (value) => {
        setDiastolicPressure(value);
        clearAccepted();
    };

    const onArmChange = (value) => {
        setArm(value);
        clearAccepted();
    };

    const onAddOptionalBP = () => {
        setOptionalBP(!optionalBP);
        clearAccepted();
    };

    const onOptionalSystolicPressureChange = (value) => {
        setOptionalSystolicPressure(value);
        clearAccepted();
    };

    const onOptionalDiastolicPressureChange = (value) => {
        setOptionalDiastolicPressure(value);
        clearAccepted();
    };

    const onOptionalArmChange = (value) => {
        setOptionalArm(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    const buttons = [];
    if (type === obsTypes.news) {
        buttons.push(
            <AgyleButton
                key="add-standing"
                fullWidth
                variant="outlined"
                colour="primary"
                onClick={onAddOptionalBP}
            >
                {optionalBP
                    ? t('Remove Standing BP')
                    : t('Add Standing BP')}
            </AgyleButton>,
        );
    }

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12}>
                        <AgyleBlockSwitch
                            onChange={onArmChange}
                            value={arm}
                            customLabels={{
                                yes: t('Left Arm'),
                                no: t(lowerExtremitiesOptions ? 'Right Leg' : 'Right Arm'),
                                empty: t('Right Arm'),
                                alternative: t('Left Leg'),
                            }}
                            valueObject={{
                                true: 'left',
                                false: lowerExtremitiesOptions ? 'rightLeg' : 'right',
                                null: 'right',
                                alt: 'leftLeg',
                            }}
                            style={{
                                backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                            }}
                            nullState={lowerExtremitiesOptions}
                            fourCases={lowerExtremitiesOptions}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={systolicPressure}
                            minValue={0}
                            maxValue={250}
                            label={t('Systolic')}
                            onChange={onSystolicPressureChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={diastolicPressure}
                            minValue={0}
                            maxValue={250}
                            label={t('Diastolic')}
                            onChange={onDiastolicPressureChange}
                        />
                    </Grid>

                    {optionalBP && (
                        <>
                            <Grid item xs={12}>
                                <AgyleBlockSwitch
                                    onChange={onOptionalArmChange}
                                    value={optionalArm}
                                    customLabels={{
                                        yes: t('Left Arm'),
                                        no: t(lowerExtremitiesOptions ? 'Right Leg' : 'Right Arm'),
                                        empty: t('Right Arm'),
                                        alternative: t('Left Leg'),
                                    }}
                                    valueObject={{
                                        true: 'left',
                                        false: lowerExtremitiesOptions ? 'rightLeg' : 'right',
                                        null: 'right',
                                        alt: 'leftLeg',
                                    }}
                                    style={{
                                        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                    }}
                                    nullState={lowerExtremitiesOptions}
                                    fourCases={lowerExtremitiesOptions}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ObsNumericStepper
                                    value={optionalSystolicPressure}
                                    minValue={0}
                                    maxValue={250}
                                    label={t('Systolic')}
                                    onChange={onOptionalSystolicPressureChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ObsNumericStepper
                                    value={optionalDiastolicPressure}
                                    minValue={0}
                                    maxValue={250}
                                    label={t('Diastolic')}
                                    onChange={onOptionalDiastolicPressureChange}
                                />
                            </Grid>
                        </>
                    )}

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: type === obsTypes.neuro,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: diastolicPressure < 1 || systolicPressure < 1 || (optionalBP && (optionalDiastolicPressure < 1 || optionalSystolicPressure < 1)),
                        showStatusNR: type === obsTypes.pews,
                        hideStatuses: type === obsTypes.neuro,
                        disableAcceptButtons,
                        customButtons: buttons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsBloodPressure.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    systolicPressure: PropTypes.number,
    diastolicPressure: PropTypes.number,
    arm: PropTypes.oneOf(['left', 'right', 'leftLeg', 'rightLeg']),
    optionalBP: PropTypes.bool,
    optionalSystolicPressure: PropTypes.number,
    optionalDiastolicPressure: PropTypes.number,
    optionalArm: PropTypes.oneOf(['left', 'right', 'leftLeg', 'rightLeg']),
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsBloodPressure.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    arm: 'left',
    optionalBP: false,
    optionalArm: 'left',
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsBloodPressure);